import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useCatalogProducts, useCatalogRoles } from '@/features/users'
import { createRole, deleteRole, getProductStructure, updateRole } from './api/api'
import { catalogRolesKey, productStructureKey } from './keyFactory'
import type { RoleInput } from './types'

// Re-exported so screens only ever import product/catalog-role reads from
// this feature's own hooks module — the sharing with the users feature is an
// implementation detail of keyFactory.ts, not something callers here need to
// know about.
export { useCatalogProducts, useCatalogRoles }

export function useProductStructure(product: string) {
  return useQuery({
    queryKey: productStructureKey(product),
    queryFn: () => getProductStructure(product),
    enabled: !!product,
  })
}

// Create/update/delete all invalidate the shared catalog-roles key for the
// product — the same read the users feature's Access tab uses, so an admin
// assigning roles right after editing them sees the change immediately.
export function useCreateRole(product: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: RoleInput) => createRole(product, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogRolesKey(product) }),
  })
}

export function useUpdateRole(product: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ roleId, input }: { roleId: string; input: RoleInput }) => updateRole(product, roleId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogRolesKey(product) }),
  })
}

export function useDeleteRole(product: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (roleId: string) => deleteRole(product, roleId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: catalogRolesKey(product) }),
  })
}
